import { getWordsCount } from '~/helpers/functions';
import { getDetectorLoadingTime } from '~/modules/tools/views/AiDetectorV4/helpers';
import { HUMANIZER_MODE_V2 } from '~/modules/tools/views/AiHumanizerV4/constants';

enum RequestLang {
  ENGLISH = 'eng',
  NON_ENGLISH = 'non-eng',
}

enum OnboardingLang {
  ENGLISH = 'en',
  NON_ENGLISH = 'non-en',
}

type LoadingConfig = {
  response_coefficient: number;
  edge_coefficient: number;
  min_time?: {
    symbol_threshold: number;
    time: number;
  }; // ms
  max_time?: number; // ms
};

export const getBypassHumanizerLoadingTime = (data: {
  requestText: string;
  requestLang: string;
  onboardingLang: string;
  mode: HUMANIZER_MODE_V2;
}): number => {
  const { requestText, requestLang, onboardingLang, mode } = data;

  const loadingConfigs: Record<
    OnboardingLang,
    Record<RequestLang, Record<HUMANIZER_MODE_V2, LoadingConfig>>
  > = {
    [OnboardingLang.ENGLISH]: {
      [RequestLang.ENGLISH]: {
        [HUMANIZER_MODE_V2.Human]: {
          response_coefficient: 0.0498,
          edge_coefficient: 4.58 * -1,
          min_time: {
            symbol_threshold: 250,
            time: 7000,
          }, //ms
          max_time: 85000, //ms
        },
        [HUMANIZER_MODE_V2.Auto]: {
          response_coefficient: 0.0103,
          edge_coefficient: 8.45,
          max_time: 27000, //ms
        },
        [HUMANIZER_MODE_V2.Detectors]: {
          response_coefficient: 0.0103,
          edge_coefficient: 8.45,
          max_time: 27000, //ms
        },
      },
      [RequestLang.NON_ENGLISH]: {
        [HUMANIZER_MODE_V2.Human]: {
          response_coefficient: 0.065,
          edge_coefficient: 24.6,
          max_time: 140000, //ms
        },
        [HUMANIZER_MODE_V2.Auto]: {
          response_coefficient: 0.059,
          edge_coefficient: 15.6,
          max_time: 121000, //ms
        },
        [HUMANIZER_MODE_V2.Detectors]: {
          response_coefficient: 0.059,
          edge_coefficient: 15.6,
          max_time: 121000, //ms
        },
      },
    },
    [OnboardingLang.NON_ENGLISH]: {
      [RequestLang.ENGLISH]: {
        [HUMANIZER_MODE_V2.Human]: {
          response_coefficient: 0.034,
          edge_coefficient: 22.1,
          max_time: 83000, //ms
        },
        [HUMANIZER_MODE_V2.Auto]: {
          response_coefficient: 0.027,
          edge_coefficient: 15.4,
          max_time: 64000, //ms
        },
        [HUMANIZER_MODE_V2.Detectors]: {
          response_coefficient: 0.027,
          edge_coefficient: 15.4,
          max_time: 64000, //ms
        },
      },
      [RequestLang.NON_ENGLISH]: {
        [HUMANIZER_MODE_V2.Human]: {
          response_coefficient: 0.065,
          edge_coefficient: 24.6,
          max_time: 140000, //ms
        },
        [HUMANIZER_MODE_V2.Auto]: {
          response_coefficient: 0.059,
          edge_coefficient: 15.6,
          max_time: 121000, //ms
        },
        [HUMANIZER_MODE_V2.Detectors]: {
          response_coefficient: 0.059,
          edge_coefficient: 15.6,
          max_time: 121000, //ms
        },
      },
    },
  };

  const requestLangType =
    requestLang === RequestLang.ENGLISH
      ? RequestLang.ENGLISH
      : RequestLang.NON_ENGLISH;

  const onboardingLangType =
    onboardingLang === OnboardingLang.ENGLISH
      ? OnboardingLang.ENGLISH
      : OnboardingLang.NON_ENGLISH;

  const config =
    loadingConfigs[onboardingLangType][requestLangType][mode] ||
    loadingConfigs[OnboardingLang.ENGLISH][RequestLang.ENGLISH][
      HUMANIZER_MODE_V2.Human
    ];

  const MAX_SYMBOLS = getHumanizerInputLimits({
    isLongHumanizer: false,
  }).maxCount;

  const wordsCount = getWordsCount(requestText);

  if (wordsCount > MAX_SYMBOLS && config.max_time) {
    return config.max_time;
  }

  if (config.min_time && wordsCount < config.min_time.symbol_threshold) {
    return config.min_time.time;
  }

  return (
    Math.round(
      config.response_coefficient * wordsCount + config.edge_coefficient,
    ) * 1000
  );
};

export const getOwnHumanizerLoadingTime = (data: {
  requestText: string;
  requestLang: string;
}): number => {
  const { requestText, requestLang } = data;

  const loadingConfigs: Record<RequestLang, LoadingConfig> = {
    [RequestLang.ENGLISH]: {
      response_coefficient: 0.0355,
      edge_coefficient: 3.2,
    },
    [RequestLang.NON_ENGLISH]: {
      response_coefficient: 0.05,
      edge_coefficient: 3.97,
    },
  };

  const requestLangType =
    requestLang === RequestLang.ENGLISH
      ? RequestLang.ENGLISH
      : RequestLang.NON_ENGLISH;

  const config =
    loadingConfigs[requestLangType] || loadingConfigs[OnboardingLang.ENGLISH];

  return (
    Math.round(
      config.response_coefficient * getWordsCount(requestText) +
        config.edge_coefficient,
    ) * 1000
  );
};

export const calculateHumanizerLoadingTime = (data: {
  requestText: string;
  requestLang: string;
  onboardingLang: string;
  mode: HUMANIZER_MODE_V2;
  isDetectorIncluded: boolean;
  isOwnHumanizer: boolean;
}): number => {
  const humanizerTime = data.isOwnHumanizer
    ? getOwnHumanizerLoadingTime({
        requestText: data.requestText,
        requestLang: data.requestLang,
      })
    : getBypassHumanizerLoadingTime({
        requestText: data.requestText,
        requestLang: data.requestLang,
        onboardingLang: data.onboardingLang,
        mode: data.mode,
      });

  if (data.isDetectorIncluded) {
    const detectorTime = getDetectorLoadingTime(
      data.requestText,
      data.requestLang,
    );
    return detectorTime + humanizerTime;
  }

  return humanizerTime;
};

export const getHumanizerWordsBalance = (): number => {
  const BALANCE = {
    standard: 80000,
  };

  return BALANCE.standard;
};

export const getHumanizerInputLimits = (data: {
  isLongHumanizer: boolean;
}): {
  minCount: number;
  maxCount: number;
} => {
  const { isLongHumanizer } = data;

  const LIMITS = {
    standard_min: 65,
    standard_max: 1800,
    own_long: 5000,
  };

  return {
    minCount: LIMITS.standard_min,
    maxCount: isLongHumanizer ? LIMITS.own_long : LIMITS.standard_max,
  };
};
